import { useUserStore } from '@/stores/userStore';
import { addToast } from '@/composables/useToasts';
import { i18n } from '@/plugins/i18n';
import type { Toast, ToastType } from '@/types/toasts';
import { camelCase } from 'lodash-es';
import { ExchangeConversationMessage } from '@/types/conversations';
import { type ApiV1, EVENT_KEYS, type DownloadStatus, type EventKey } from '@teamworksdev/influencer/core';

// Message deduplication cache
const processedMessageIds = new Map<string, number>();
const MESSAGE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const cleanupOldMessages = () => {
	const now = Date.now();
	for (const [id, timestamp] of processedMessageIds.entries()) {
		if (now - timestamp > MESSAGE_CACHE_TTL) {
			processedMessageIds.delete(id);
		}
	}
};

const isMessageAlreadyProcessed = (messageUuid: string): boolean => {
	cleanupOldMessages();

	if (processedMessageIds.has(messageUuid)) {
		return true;
	}

	processedMessageIds.set(messageUuid, Date.now());
	return false;
};

const displayToast = (translation: ApiV1.EventTranslation, type: ToastType = 'success', link?: string) => {
	const translationKey = camelCase(translation.key);
	const titlePath = `websocketNotifications.${translationKey}.title`;
	const descriptionPath = `websocketNotifications.${translationKey}.description`;
	const descriptionExists = i18n.global.te(descriptionPath);

	const toast: Omit<Toast, 'uuid'> = {
		description: descriptionExists
			? i18n.global.t(descriptionPath, { ...translation.data })
			: undefined,
		title: i18n.global.t(titlePath, { ...translation.data }),
		type,
		link,
	};

	addToast(toast);
};

const handleMediaDownloadProgress = (event: ApiV1.WebsocketEvent) => {
	if (event.eventKey !== 'media.download.progress') return;

	const downloadStatusToasts: Partial<Record<DownloadStatus, () => void>> = {
		finished: () => displayToast({ key: 'mediaDownloadComplete' }),
		failed: () => displayToast({ key: 'mediaDownloadFailed' }, 'error'),
	};

	downloadStatusToasts[event.data.status]?.();
};

const handleConversationCommentCreated = (event: ApiV1.WebsocketEvent) => {
	if (event.eventKey !== 'conversation.comment.created') return;

	// Check for duplicate messages
	const messageUuid = event.data.message?.uuid;
	if (!messageUuid || isMessageAlreadyProcessed(messageUuid)) {
		return;
	}

	const userStore = useUserStore();
	userStore.receivedConversationEvent = {
		conversationUuid: event.data.conversation_uuid,
		message: new ExchangeConversationMessage(event.data.message),
		senderName: `${event.data.sender.first_name} ${event.data.sender.last_name}`,
	};
};

const handleBulkPaymentCompleted = (event: ApiV1.WebsocketEvent) => {
	if (event.eventKey !== 'bulk.payment.completed') return;
	const { payments_successful, payments_failed, total } = event.data;

	let title = i18n.global.t('websocketNotifications.bulkPaymentCompleted.title');
	let description: string;
	let type: ToastType;
	let timeout = 5000;

	if (total === payments_successful) {
		description = i18n.global.t('websocketNotifications.bulkPaymentCompleted.all');
		type = 'success';
	} else if (total === payments_failed) {
		title = i18n.global.t('websocketNotifications.bulkPaymentCompleted.titleFailed');
		description = i18n.global.t('websocketNotifications.bulkPaymentCompleted.allFailed');
		type = 'error';
		timeout *= 2;
	} else {
		description = i18n.global.t('websocketNotifications.bulkPaymentCompleted.someSucceeded', { payments_successful, total });
		type = 'warning';
		timeout *= 2;
	}

	const toast: Omit<Toast, 'uuid'> = {
		title,
		description,
		type,
	};

	addToast(toast, timeout);
};

const handleWebsocketEvent = (event: ApiV1.WebsocketEvent) => {
	switch (event.eventKey) {
		case 'gallery.duplicate.completed':
		case 'media.delete.complete':
		case 'exchange.transaction.create.completed':
			return displayToast({ key: event.eventKey });

		case 'gallery.duplicate.failed':
		case 'media.delete.failed':
		case 'media.update.failed':
			return displayToast({ key: event.eventKey }, 'error');

		case 'media.download.progress':
			return handleMediaDownloadProgress(event);

		case 'conversation.comment.created':
			return handleConversationCommentCreated(event);

		case 'bulk.payment.completed':
			return handleBulkPaymentCompleted(event);
	}
};

export const handleUserChannelWebSocketNotification = (eventName: EventKey, data: ApiV1.WebsocketEvent) => {
	const userStore = useUserStore();
	if (!userStore.profile?.uuid || data.metadata?.profile_context !== userStore.profile?.uuid) return;

	const eventKey = EVENT_KEYS.find((e) => e === eventName);
	if (!eventKey) return;

	handleWebsocketEvent({ ...data, eventKey } as ApiV1.WebsocketEvent);
};
